import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Dropdown, Segmented, Skeleton, Space } from 'antd';
import { DownOutlined, SendOutlined } from '@ant-design/icons';
import type { ApprovalKind, StudioOverview } from '../contract';
import {
  STUDIO_PAGE_LIMIT,
  StudioLoad,
  StudioViewer,
  getApprovalsMe,
  getApprovalsQueue,
  getApprovalsSummary,
  getRequestsQueue,
  getStudioRequests,
  studioAbilities,
  useStudioLoad,
} from '../api';
import {
  APPROVAL_KIND_LABEL,
  EMPTY_TEXT,
  OT_ON_APP_NOTE,
  STUDIO_TAB_KINDS,
  kindOffText,
  pendingRequestsLine,
  pendingRequestsLineSmod,
} from '../labels';
import CountPill from '../components/count-pill';
import ApprovalTable from '../components/approval-table';
import RequestsTable from '../components/requests-table';
import RequestModal from '../components/request-modal';
import { StudioLoadError } from './studio-header';
import styles from '../styles.module.scss';

export interface QueueTabProps {
  studioId: number;
  viewer: StudioViewer;
  /** S9 của trang: `pending.requests` cho tiêu đề khối "Đề nghị gửi Admin". */
  overview: StudioLoad<StudioOverview>;
  /** Mở tab khác (đề nghị gỡ người / nhập KC làm ở tab Thành viên, gắn kênh ở tab Nhóm nhảy). */
  onOpenTab: (tab: 'members' | 'teams') => void;
  /** Sau mỗi thao tác: tải lại S9 để số `todo` trên nhãn tab và ô Tổng quan khớp. */
  onChanged?: () => void;
}

/** Thứ tự loại việc trong tab (SHOP không có: giao quà làm tại quầy bằng QR trên app). */
const KIND_ORDER: ApprovalKind[] = ['LEAVE', 'CLAIM', 'OT', 'WAIVE'];
/** Số chưa biết (S9 đang tải hoặc lỗi). */
const UNKNOWN_COUNT = '—';
const OT_PAGE = '/overtime-management';
const WAIVE_PAGE = '/star/penalties?tab=waives';

/**
 * Tab Cần xử lý của Chi tiết Studio (SPEC Studio mục 8.7.8, 7.5).
 * Khối 1 "Đơn của thành viên": loại việc từ GET /approvals/me (bỏ SHOP), số từ /approvals/summary?studioId,
 * bảng /approvals/queue?kind&studioId; chỉ LEAVE, CLAIM có nút (ApprovalTable). Loại việc đang tắt cho Quản lý
 * thì hiện chú thích thay vì im lặng.
 * Khối 2 "Đề nghị gửi Admin": Quản lý xem đề nghị của Studio mình (S25) + nút Gửi đề nghị; Admin / SMOD xem
 * hàng đợi Q2 lọc theo Studio (không gửi `kind`), Admin duyệt ngay tại đây, SMOD chỉ xem.
 */
const QueueTab = ({ studioId, viewer, overview, onOpenTab, onChanged }: QueueTabProps) => {
  const abilities = studioAbilities(viewer.mine, studioId);
  const isManager = abilities.request;

  // ---------------------------------------------------- khối 1: đơn thành viên
  const me = useStudioLoad(() => getApprovalsMe(), []);
  const summary = useStudioLoad(() => getApprovalsSummary(studioId), [studioId], {
    resetKey: studioId,
  });
  const kinds = KIND_ORDER.filter((k) => (me.data?.kinds || []).includes(k));
  const offKinds = me.data && isManager ? KIND_ORDER.filter((k) => !kinds.includes(k)) : [];

  const [kind, setKind] = useState<ApprovalKind | null>(null);
  const [page, setPage] = useState(1);
  const activeKind = kind && kinds.includes(kind) ? kind : kinds[0] ?? null;
  // Tăng ca: Quản lý Studio duyệt trên app, ở đây chỉ thấy số (mục 8.7.8).
  const showTable = !!activeKind && !(activeKind === 'OT' && isManager);

  const queue = useStudioLoad(
    () =>
      getApprovalsQueue({
        kind: activeKind as ApprovalKind,
        studioId,
        page,
        limit: STUDIO_PAGE_LIMIT,
      }),
    [studioId, activeKind, page],
    { enabled: showTable, resetKey: `${studioId}:${activeKind}` }
  );

  const afterMemberAction = () => {
    queue.reload();
    summary.reload();
    onChanged?.();
  };

  const counts = summary.data?.counts;
  const kindNote = (() => {
    if (activeKind === 'OT') {
      return isManager ? (
        <Alert type="info" showIcon message={OT_ON_APP_NOTE} />
      ) : (
        <Alert
          type="info"
          showIcon
          message="Duyệt tăng ca ở trang Tăng ca."
          action={
            <Link to={OT_PAGE}>
              <Button size="small">Mở trang Tăng ca</Button>
            </Link>
          }
        />
      );
    }
    if (activeKind === 'WAIVE') {
      return (
        <Alert
          type="info"
          showIcon
          message="Duyệt xin xoá phạt ở trang Phạt."
          action={
            <Link to={WAIVE_PAGE}>
              <Button size="small">Mở trang Phạt</Button>
            </Link>
          }
        />
      );
    }
    return null;
  })();

  // ----------------------------------------------------- khối 2: đề nghị
  const [reqPage, setReqPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const mine = useStudioLoad(
    () => getStudioRequests(studioId, { status: 'ALL', page: reqPage, limit: STUDIO_PAGE_LIMIT }),
    [studioId, reqPage],
    { enabled: isManager, resetKey: studioId }
  );
  const adminQueue = useStudioLoad(
    () => getRequestsQueue({ studioId, page: reqPage, limit: STUDIO_PAGE_LIMIT }),
    [studioId, reqPage],
    { enabled: viewer.canViewAll, resetKey: studioId }
  );
  const requests = isManager ? mine : adminQueue;
  // Số đề nghị chờ lấy từ S9; S9 chưa có (đang tải / lỗi) thì hiện "—", không lấy 0.
  const pendingCount = overview.data ? overview.data.pending.requests : null;
  const overviewFailed = !!overview.error && !overview.data;

  const afterRequestAction = () => {
    requests.reload();
    onChanged?.();
  };

  const requestsTitle = isManager ? (
    pendingCount !== null ? (
      pendingRequestsLine(pendingCount)
    ) : (
      `Đề nghị đang chờ Admin: ${UNKNOWN_COUNT}`
    )
  ) : viewer.isSmod ? (
    pendingCount !== null ? (
      pendingRequestsLineSmod(pendingCount)
    ) : (
      `Đề nghị chờ Admin: ${UNKNOWN_COUNT}`
    )
  ) : (
    <span>
      Đề nghị gửi Admin{' '}
      {overviewFailed ? UNKNOWN_COUNT : <CountPill count={pendingCount} hideZero />}
    </span>
  );

  return (
    <div className={styles.page}>
      <Card size="small" title="Đơn của thành viên">
        <div className={styles.page}>
          {me.error && <StudioLoadError error={me.error} onRetry={me.reload} />}
          {offKinds.length > 0 && (
            <div className={styles.notes}>
              {offKinds.map((k) => (
                <Alert key={k} type="info" showIcon message={kindOffText(k)} />
              ))}
            </div>
          )}
          {me.loading && !me.data ? (
            <Skeleton active paragraph={{ rows: 3 }} />
          ) : kinds.length ? (
            <>
              <Segmented<ApprovalKind>
                value={activeKind ?? undefined}
                onChange={(value) => {
                  setKind(value);
                  setPage(1);
                }}
                options={kinds.map((k) => ({
                  value: k,
                  label: (
                    <Space size={6}>
                      <span>{APPROVAL_KIND_LABEL[k]}</span>
                      <CountPill
                        count={counts?.[k]}
                        tone={STUDIO_TAB_KINDS.includes(k) ? 'danger' : 'muted'}
                      />
                    </Space>
                  ),
                }))}
              />
              {summary.error && <StudioLoadError error={summary.error} onRetry={summary.reload} />}
              {kindNote}
              {showTable && activeKind && (
                <>
                  {queue.error && <StudioLoadError error={queue.error} onRetry={queue.reload} />}
                  <ApprovalTable
                    kind={activeKind}
                    rows={queue.data?.data || []}
                    loading={queue.loading}
                    onChanged={afterMemberAction}
                    pagination={
                      queue.data
                        ? {
                            page,
                            pageSize: STUDIO_PAGE_LIMIT,
                            total: queue.data.metadata.total,
                            onChange: setPage,
                          }
                        : undefined
                    }
                  />
                </>
              )}
            </>
          ) : (
            !me.error && <div className={styles.muted}>{EMPTY_TEXT.queueMembers}</div>
          )}
        </div>
      </Card>

      {(isManager || viewer.canViewAll) && (
        <Card
          size="small"
          title={requestsTitle}
          extra={
            isManager ? (
              <Dropdown
                trigger={['click']}
                menu={{
                  items: [
                    { key: 'add', label: 'Thêm người vào Studio' },
                    {
                      key: 'members',
                      label: 'Gỡ người hoặc nhập kim cương (chọn ở tab Thành viên)',
                    },
                    { key: 'teams', label: 'Gắn kênh Nhóm nhảy (chọn ở tab Nhóm nhảy)' },
                  ],
                  onClick: ({ key }) => {
                    if (key === 'add') setAdding(true);
                    else if (key === 'members' || key === 'teams') onOpenTab(key);
                  },
                }}
              >
                <Button type="primary" icon={<SendOutlined />}>
                  Gửi đề nghị <DownOutlined />
                </Button>
              </Dropdown>
            ) : undefined
          }
        >
          {overviewFailed && <StudioLoadError error={overview.error} onRetry={overview.reload} />}
          {requests.error && <StudioLoadError error={requests.error} onRetry={requests.reload} />}
          <RequestsTable
            mode={isManager ? 'mine' : 'queue'}
            showStudio={false}
            selectable={abilities.admin}
            rows={requests.data?.data || []}
            loading={requests.loading}
            onChanged={afterRequestAction}
            emptyText={EMPTY_TEXT.queueRequests}
            pagination={
              requests.data
                ? {
                    page: reqPage,
                    pageSize: STUDIO_PAGE_LIMIT,
                    total: requests.data.metadata.total,
                    onChange: setReqPage,
                  }
                : undefined
            }
          />
        </Card>
      )}

      {isManager && adding && (
        <RequestModal
          open
          studioId={studioId}
          requestKind="MEMBER_ADD"
          onClose={() => setAdding(false)}
          onDone={afterRequestAction}
        />
      )}
    </div>
  );
};

export default QueueTab;
